import { Garnish } from "@/shared/utils/interfaces";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useImmer } from "use-immer";
import { getLabel, includesSearch, reorder } from "../../shared/utils/helpers";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Search from "../Input/Search";

interface SelectListProps {
  garnishes: Garnish[];
  value?: string;
  defaultValue: any;
  onSubmit: (value: any) => void;
  isPending: boolean;
}

const List = (props) => (
  <div
    className="h-92 overflow-scroll"
    style={{
      boxShadow:
        "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
    }}
    {...props}
  />
);

const ItemContainer = (props) => (
  <div
    className="cursor-pointer flex items-center px-12 py-6 hover:bg-grey"
    {...props}
  />
);

const Item = React.forwardRef<any, any>(
  (
    { onUpClick, onDownClick, active, selected, label, onClick, ...props },
    ref
  ) => (
    <ItemContainer
      onClick={!active ? onClick : undefined}
      ref={ref}
      {...props}
      alignItems="center"
      py="1rem"
    >
      {active && (
        <div className="mr-[2rem]">
          <div onClick={onUpClick}>▲</div>
          <div onClick={onDownClick}>▼</div>
        </div>
      )}
      <Checkbox onClick={active ? onClick : undefined} active={selected} />
      <div className="ml-[1rem]">{label}</div>
    </ItemContainer>
  )
);

const ButtonContainer = (props) => (
  <div
    className="p-12 [&_button]:flex [&_button]:w-20 [&_button]:items-center [&_button]:justify-center [&_button]:rotate-90 lg:[&_button]:rotate-0 lg:[&_button]:mb-4 lg:[&_button:last-child]:mb-0"
    {...props}
  />
);

const SelectList = ({
  garnishes,
  defaultValue,
  value = "item_id",
  isPending,
  onSubmit,
}: SelectListProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useImmer({
    selected: "",
    active: "",
  });
  const [selected, setSelected] = useState<any[]>([]);
  const [active, setActive] = useState<any[]>([]);

  useEffect(() => {
    if (defaultValue) {
      setActive(defaultValue.map((v) => v.item_id));
    } else {
      setActive([]);
    }
    // eslint-disable-next-line
  }, [defaultValue]);

  const handleSelect = (v: any) => {
    selected.includes(v)
      ? setSelected((selected) => selected.filter((s) => s !== v))
      : setSelected((selected) => [...selected, v]);
  };

  const setAllActive = () => {
    setActive(garnishes.map((d) => d[value]));
  };

  const handleActive = () => {
    const filteredSelected = selected.filter((id) => !active.includes(id));
    setActive((active) => [...active, ...filteredSelected]);
    setSelected((s) => s.filter((id) => !filteredSelected.includes(id)));
  };

  const handleDisable = () => {
    const filteredSelected = selected.filter((id) => active.includes(id));
    setActive((active) =>
      active.filter((id) => !filteredSelected.includes(id))
    );
    setSelected((s) => s.filter((id) => !filteredSelected.includes(id)));
  };

  return (
    <>
      <div className="flex flex-wrap justify-center">
        <div className="min-w-full lg:min-w-[40%]">
          <div className="mb-4 flex flex-wrap items-center">
            <div className="text-2xl font-semibold">{t("selected")}</div>

            <div className="ml-[1rem]">
              <Search
                onDeleteClick={() =>
                  setSearch((draft) => {
                    draft.active = "";
                  })
                }
                value={search.active}
                onChange={(e) =>
                  setSearch((draft) => {
                    draft.active = e.target.value;
                  })
                }
                placeholder={t("filter")}
              />
            </div>
          </div>

          <List>
            {garnishes
              ?.sort(
                (a, b) =>
                  active?.indexOf(a.item_id) - active?.indexOf(b.item_id)
              )
              ?.filter((garnish) =>
                search.active !== ""
                  ? includesSearch(garnish, search.active)
                  : true
              )
              .filter((garnish) => active.includes(garnish[value]))
              .map((garnish, i) => (
                <Item
                  onUpClick={() =>
                    setActive((active) => reorder(active, i, i - 1))
                  }
                  onDownClick={() =>
                    setActive((active) => reorder(active, i, i + 1))
                  }
                  active={active.includes(garnish[value])}
                  onClick={() => handleSelect(garnish[value])}
                  key={garnish[value]}
                  selected={selected.includes(garnish[value])}
                  value={garnish[value]}
                  label={getLabel(garnish)}
                />
              ))}
          </List>
        </div>

        <ButtonContainer className="mt-[2.8rem] flex justify-center flex-row lg:flex-col gap-4">
          <Button
            onClick={() => setAllActive()}
            isDisabled={active?.length === garnishes.length}
          >
            {"≪"}
          </Button>
          <Button
            onClick={() => handleActive()}
            isDisabled={
              selected.filter((id) => !active?.includes(id)).length === 0
            }
          >
            {"<"}
          </Button>
          <Button
            onClick={() => handleDisable()}
            isDisabled={
              selected.filter((id) => active?.includes(id)).length === 0
            }
          >
            {">"}
          </Button>
          <Button
            onClick={() => setActive([])}
            isDisabled={active?.length === 0}
          >
            ≫
          </Button>
        </ButtonContainer>

        <div className="min-w-full lg:min-w-[40%]">
          <div className="mb-4 flex flex-wrap items-center">
            <div className="text-2xl font-semibold">
              {t("menu.extraCategory")}
            </div>

            <div className="ml-[1rem]">
              <Search
                onDeleteClick={() =>
                  setSearch((draft) => {
                    draft.selected = "";
                  })
                }
                value={search.selected}
                onChange={(e) =>
                  setSearch((draft) => {
                    draft.selected = e.target.value;
                  })
                }
                placeholder={t("filter")}
              />
            </div>
          </div>

          <List>
            {garnishes
              ?.filter((garnish) => !active?.includes(garnish[value]))
              ?.filter((garnish) =>
                search.selected !== ""
                  ? includesSearch(garnish, search.selected)
                  : true
              )
              ?.map((garnish) => (
                <Item
                  onClick={() => handleSelect(garnish[value])}
                  key={garnish[value]}
                  selected={selected.includes(garnish[value])}
                  value={garnish[value]}
                  label={getLabel(garnish)}
                />
              ))}
          </List>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button isPending={isPending} onClick={() => onSubmit(active)}>
          {t("save")}
        </Button>
      </div>
    </>
  );
};

export default SelectList;
